import { Fragment } from 'react'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { DateColumnPicker } from './date-column-picker'
import type { ReactNode } from 'react'
import type {
  ColumnFigures,
  ColumnMode,
  DateColumn,
  ProposalRow,
  ProposalSort,
  ProposalView,
  SortKey,
  TakenCell,
} from '@/features/reports/lib/order-proposal-math'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  isSingleDay,
  takenQuantity,
} from '@/features/reports/lib/order-proposal-math'
import {
  dishDayLabel,
  formatOrderDays,
  formatQuantity,
} from '@/features/reports/lib/order-proposal-labels'
import { cn } from '@/lib/utils'

export interface TableColumn {
  mode: ColumnMode
  column: DateColumn
}

const NUM = 'px-3 py-2 text-right tabular-nums whitespace-nowrap'
const SEP = 'border-l border-border'
const SKU =
  'px-3 py-2 text-xs text-muted-foreground tabular-nums whitespace-nowrap'

function spanOf(column: DateColumn): number {
  return isSingleDay(column) ? 2 : 4
}

function quantity(value: number | null): string {
  return value == null ? '–' : formatQuantity(value)
}

/** Taking one Vorschlag cell over into its row's Bestellung; absent without the order permission. */
interface CellTake {
  /** Whether the row's Bestellung is this cell's value, taken from here. */
  taken: (cell: TakenCell, value: number) => boolean
  onTake: (cell: TakenCell, value: number) => void
  label: (value: string) => string
}

/**
 * A proposal: highlighted when there is something to order, quiet at 0. With the order
 * permission a click takes the value over into the Bestellung; the taken cell stays marked.
 */
function ProposalCell({
  value,
  cell,
  take,
}: {
  value: number | null
  cell: TakenCell
  take?: CellTake
}) {
  if (value == null) return <td className={NUM} />
  const tone =
    value > 0
      ? 'bg-primary/5 font-semibold text-primary'
      : 'text-muted-foreground'
  if (!take) return <td className={cn(NUM, tone)}>{formatQuantity(value)}</td>
  const taken = take.taken(cell, value)
  return (
    <td className={cn(tone, 'p-0', taken && 'bg-primary/15')}>
      <button
        type="button"
        onClick={() => take.onTake(cell, value)}
        aria-pressed={taken}
        aria-label={take.label(formatQuantity(value))}
        className={cn(
          NUM,
          'w-full cursor-pointer hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset',
          taken && 'ring-1 ring-primary ring-inset',
        )}
      >
        {formatQuantity(value)}
      </button>
    </td>
  )
}

/** Inventur, Zukauf and Gesamt; Zukauf 0 and missing stock show as "–". */
function StockCells({
  onHand,
  incoming,
  gesamt,
}: {
  onHand: number | null
  incoming: number | null
  gesamt: number
}) {
  const noStock = onHand == null && incoming == null
  return (
    <>
      <td className={cn(NUM, onHand == null && 'text-muted-foreground')}>
        {quantity(onHand)}
      </td>
      <td className={cn(NUM, !incoming && 'text-muted-foreground')}>
        {incoming ? formatQuantity(incoming) : '–'}
      </td>
      <td
        className={cn(NUM, 'font-semibold', noStock && 'text-muted-foreground')}
      >
        {noStock ? '–' : formatQuantity(gesamt)}
      </td>
    </>
  )
}

function FigureCells({
  figures,
  column,
  index,
  expected,
  take,
}: {
  figures: ColumnFigures
  column: DateColumn
  index: number
  expected?: boolean
  take?: CellTake
}) {
  const shown = (value: number) =>
    expected ? `≈ ${formatQuantity(value)}` : formatQuantity(value)
  return (
    <>
      <td className={cn(NUM, SEP, expected && 'text-primary')}>
        {shown(figures.sale)}
      </td>
      {isSingleDay(column) ? (
        <ProposalCell
          value={figures.proposal}
          cell={`${index}:sum`}
          take={take}
        />
      ) : (
        <>
          <td className={cn(NUM, expected && 'text-primary')}>
            {figures.average == null ? '' : shown(figures.average)}
          </td>
          <ProposalCell
            value={figures.proposal}
            cell={`${index}:sum`}
            take={take}
          />
          <ProposalCell
            value={figures.proposalAvg}
            cell={`${index}:avg`}
            take={take}
          />
        </>
      )}
    </>
  )
}

function GroupRow({ span, children }: { span: number; children: ReactNode }) {
  return (
    <tr className="border-b border-border">
      <th
        colSpan={span}
        scope="colgroup"
        className="px-3 pt-4 pb-1.5 text-left text-xs font-bold tracking-wide uppercase"
      >
        {children}
      </th>
    </tr>
  )
}

/**
 * The Bestellvorschlag table (CHG-2026-10-bestellvorschlag, canvas "Final"): the date column
 * pickers in the header, then "Tagesgerichte · zu bestellen", "Tagesgerichte · verkauft" and the
 * category groups. Everything it shows comes from {@link ProposalView}; the Bestellung inputs are
 * the page's state. A Vorschlag cell clicked with the order permission becomes its row's Bestellung.
 */
export function OrderProposalTable({
  columns,
  onColumnChange,
  sort,
  onSort,
  view,
  loading,
  message,
  canOrder,
  orders,
  onOrderChange,
  taken,
  onTake,
  soldOpen,
  onSoldOpenChange,
}: {
  columns: Array<TableColumn>
  onColumnChange: (index: number, mode: ColumnMode) => void
  sort: ProposalSort
  onSort: (key: SortKey) => void
  view: ProposalView | null
  loading: boolean
  /** Shown in place of the rows: an error or the empty state. */
  message: ReactNode
  canOrder: boolean
  orders: Record<string, string>
  onOrderChange: (key: string, value: string) => void
  taken: Record<string, TakenCell>
  onTake: (key: string, cell: TakenCell, value: number) => void
  soldOpen: boolean
  onSoldOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('reports')
  const span =
    5 +
    columns.reduce((sum, c) => sum + spanOf(c.column), 0) +
    (canOrder ? 1 : 0)

  const orderCell = (row: { key: string; name: string }) =>
    canOrder ? (
      <td className={cn(SEP, 'px-3 py-1 text-right')}>
        <Input
          type="number"
          min={0}
          step={1}
          inputMode="numeric"
          value={orders[row.key] ?? ''}
          onChange={(event) => onOrderChange(row.key, event.target.value)}
          aria-label={t('orderProposal.orderInput', { name: row.name })}
          className="ml-auto h-8 w-20 text-right tabular-nums print:hidden"
        />
        <span className="hidden tabular-nums print:inline">
          {(orders[row.key] ?? '').replace('.', ',')}
        </span>
      </td>
    ) : null

  const takeOf = (row: ProposalRow): CellTake | undefined =>
    canOrder
      ? {
          taken: (cell, value) =>
            taken[row.key] === cell &&
            (orders[row.key] ?? '') === takenQuantity(value),
          onTake: (cell, value) => onTake(row.key, cell, value),
          label: (value) => t('orderProposal.take', { value, name: row.name }),
        }
      : undefined

  const proposalRow = (row: ProposalRow, expected?: boolean) => (
    <tr key={row.key} className="border-b border-border last:border-0">
      <td className="px-3 py-2">
        {row.name}
        {row.orderDays ? (
          <span className="ml-1.5 text-xs text-muted-foreground">
            {formatOrderDays(row.orderDays)}
          </span>
        ) : null}
      </td>
      <td className={SKU}>{row.sku}</td>
      <StockCells
        onHand={row.onHand}
        incoming={row.incoming}
        gesamt={row.gesamt}
      />
      {row.columns.map((figures, i) => (
        <FigureCells
          key={i}
          figures={figures}
          column={columns[i].column}
          index={i}
          expected={expected}
          take={takeOf(row)}
        />
      ))}
      {orderCell(row)}
    </tr>
  )

  const emptyCells = (column: DateColumn) =>
    Array.from({ length: spanOf(column) - 1 }, (_, i) => <td key={i} />)

  return (
    // The table scrolls inside its own box, which takes the height the page leaves it, so the page
    // top (filters, fill bar) and the header rows stay put while scrolling (a sticky header does not
    // stick inside an overflow-x box that does not scroll vertically). Print lifts the box.
    <div
      className="min-h-0 w-full flex-1 overflow-auto rounded-xl border border-border print:flex-none print:overflow-visible"
      data-print-fit
    >
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-muted shadow-[0_1px_0_var(--border)]">
          <tr>
            <th scope="col" colSpan={2} />
            <th scope="col" colSpan={3} />
            {columns.map((c, i) => (
              <th
                key={i}
                scope="colgroup"
                colSpan={spanOf(c.column)}
                className={cn(SEP, 'px-3 pt-2 text-left font-normal')}
              >
                <div className="flex items-center gap-1.5">
                  <DateColumnPicker
                    mode={c.mode}
                    column={c.column}
                    onChange={(mode) => onColumnChange(i, mode)}
                  />
                </div>
              </th>
            ))}
            {canOrder ? <th scope="col" className={SEP} /> : null}
          </tr>
          <tr className="border-b border-border text-xs text-muted-foreground">
            {(
              [
                ['name', 'article'],
                ['sku', 'sku'],
                ['onHand', 'onHand'],
                ['incoming', 'incoming'],
                ['gesamt', 'total'],
              ] as const
            ).map(([key, label], i) => (
              <SortHead
                key={key}
                sortKey={key}
                label={t(`orderProposal.columns.${label}`)}
                numeric={i >= 2}
                sort={sort}
                onSort={onSort}
              />
            ))}
            {columns.map((c, i) => (
              <Fragment key={i}>
                {(isSingleDay(c.column)
                  ? ([
                      ['sale', 'sales'],
                      ['proposal', 'proposal'],
                    ] as const)
                  : ([
                      ['sale', 'sales'],
                      ['average', 'average'],
                      ['proposal', 'proposalSum'],
                      ['proposalAvg', 'proposalAvg'],
                    ] as const)
                ).map(([key, label], j) => (
                  <SortHead
                    key={key}
                    sortKey={key}
                    label={t(`orderProposal.columns.${label}`)}
                    numeric
                    separator={j === 0}
                    sort={sort}
                    onSort={onSort}
                  />
                ))}
              </Fragment>
            ))}
            {canOrder ? (
              <th scope="col" className={cn(NUM, SEP, 'font-semibold')}>
                {t('orderProposal.columns.order')}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={span} className="p-3">
                <Skeleton className="h-72 w-full" />
              </td>
            </tr>
          ) : message ? (
            <tr>
              <td
                colSpan={span}
                className="py-16 text-center text-sm text-muted-foreground"
              >
                {message}
              </td>
            </tr>
          ) : view ? (
            <>
              {view.showTagesgerichte ? (
                <>
                  <GroupRow span={span}>
                    {t('orderProposal.groups.toOrder')}
                  </GroupRow>
                  {view.planned.map((row) => proposalRow(row, true))}

                  <GroupRow span={span}>
                    {t('orderProposal.groups.sold')}
                  </GroupRow>
                  <tr className="border-b border-border bg-muted/50">
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="flex items-center gap-1.5 text-left font-semibold"
                        aria-expanded={soldOpen}
                        onClick={() => onSoldOpenChange(!soldOpen)}
                      >
                        {soldOpen ? (
                          <ChevronDown
                            className="h-4 w-4 print:hidden"
                            aria-hidden="true"
                          />
                        ) : (
                          <ChevronRight
                            className="h-4 w-4 print:hidden"
                            aria-hidden="true"
                          />
                        )}
                        {t('orderProposal.allTagesgerichte')}
                      </button>
                    </td>
                    <td colSpan={4} />
                    {view.soldTotal.columns.map((figures, i) => (
                      <SoldTotalCells
                        key={i}
                        sale={figures.sale}
                        average={figures.average}
                        column={columns[i].column}
                      />
                    ))}
                    {canOrder ? <td className={SEP} /> : null}
                  </tr>
                  {view.sold.map((dish) => (
                    <tr
                      key={dish.key}
                      // Collapsed on screen, always expanded on paper (D12).
                      className={cn(
                        'border-b border-border text-muted-foreground',
                        !soldOpen && 'hidden print:table-row',
                      )}
                    >
                      <td className="py-2 pr-3 pl-9">
                        {dish.name}
                        <span className="block text-xs">
                          {dishDayLabel(t, dish)}
                        </span>
                      </td>
                      <td className={SKU}>{dish.sku}</td>
                      <StockCells
                        onHand={dish.onHand}
                        incoming={dish.incoming}
                        gesamt={dish.gesamt}
                      />
                      {dish.sales.map((sale, i) => (
                        <SoldDishCells
                          key={i}
                          sale={sale}
                          empty={emptyCells(columns[i].column)}
                        />
                      ))}
                      {canOrder ? <td className={SEP} /> : null}
                    </tr>
                  ))}
                </>
              ) : null}

              {view.groups.map((group) => (
                <Fragment key={group.key}>
                  <GroupRow span={span}>
                    {group.name ?? t('orderProposal.noCategory')}
                  </GroupRow>
                  {group.rows.map((row) => proposalRow(row))}
                </Fragment>
              ))}
            </>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}

/** A heading that sorts by its column; the active one shows its direction (D4). */
function SortHead({
  sortKey,
  label,
  numeric,
  separator,
  sort,
  onSort,
}: {
  sortKey: SortKey
  label: string
  numeric?: boolean
  separator?: boolean
  sort: ProposalSort
  onSort: (key: SortKey) => void
}) {
  const active = sort.key === sortKey
  const Arrow = sort.dir === 'asc' ? ArrowUp : ArrowDown
  return (
    <th
      scope="col"
      aria-sort={
        active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
      }
      className={cn(
        numeric ? NUM : 'px-3 py-2 text-left',
        'font-semibold',
        separator && SEP,
      )}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 rounded-sm hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          active && 'text-foreground',
        )}
      >
        {label}
        {active ? (
          <Arrow className="h-3 w-3 print:hidden" aria-hidden="true" />
        ) : null}
      </button>
    </th>
  )
}

function SoldTotalCells({
  sale,
  average,
  column,
}: {
  sale: number
  average: number | null
  column: DateColumn
}) {
  return (
    <>
      <td className={cn(NUM, SEP, 'font-semibold')}>{formatQuantity(sale)}</td>
      {isSingleDay(column) ? (
        <td />
      ) : (
        <>
          <td className={cn(NUM, 'font-semibold')}>
            {average == null ? '' : formatQuantity(average)}
          </td>
          <td />
          <td />
        </>
      )}
    </>
  )
}

function SoldDishCells({
  sale,
  empty,
}: {
  sale: number | null
  empty: Array<ReactNode>
}) {
  return (
    <>
      <td className={cn(NUM, SEP)}>{quantity(sale)}</td>
      {empty}
    </>
  )
}
